import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import client from "../lib/api";
import { useApp } from "../context/AppContext";
import { useNarrator, fetchNarration } from "../lib/narration";
import { useDemoTracking } from "../lib/analytics";
import { Hand, Play, Pause, SkipBack, SkipForward, RotateCcw, Volume2, VolumeX, Loader2 } from "lucide-react";

// Generic "faithful replica" walkthrough. Fetches the verbatim manual for `app`, optionally
// prepends `prefixSteps` (rendered with `PrefixPane`, e.g. the Master Enabler onboarding), then
// renders each app step with `Pane`, moving an invisible-hand + ring onto the control named in
// target[appStepIndex] (or the prefix step's own rid).
export default function ReplicaTour({ app, Pane, target, note, meTargets = [],
  prefixSteps = [], PrefixPane = null, appLabel = "The application", prefixLabel = "Get started" }) {
  const { lang } = useApp();
  const [sp] = useSearchParams();
  const [steps, setSteps] = useState([]);
  const [i, setI] = useState(0);
  const [playing, setPlaying] = useState(true);
  const [voiceOn, setVoiceOn] = useState(sp.get("voice") === "1");
  const [box, setBox] = useState(null);
  const wrapRef = useRef(null);
  const activeRef = useRef(null);

  useEffect(() => {
    setI(0); setPlaying(true); setBox(null);
    client.get(`/site/manual-steps/${app}`, { params: { lang } }).then((r) => setSteps(r.data.steps || [])).catch(() => {});
  }, [app, lang]);

  const [onboardingT, setOnboardingT] = useState([]);
  const [ui, setUi] = useState({});
  const [meSection, setMeSection] = useState("");
  const [labels, setLabels] = useState({});
  useEffect(() => {
    if (lang === "ENG") { setOnboardingT([]); setUi({}); setMeSection(""); setLabels({}); return; }
    client.get("/site/demo-i18n", { params: { lang } })
      .then((r) => { setOnboardingT(r.data.onboarding || []); setUi(r.data.ui || {}); setMeSection(r.data.me_section || ""); setLabels(r.data.labels || {}); })
      .catch(() => { setOnboardingT([]); setUi({}); setMeSection(""); setLabels({}); });
  }, [lang]);
  const L = (k, fb) => ui[k] || fb;
  const P = useCallback((t) => labels[t] || t, [labels]);
  const effPrefix = useMemo(() => prefixSteps.map((p, k) => {
    const tr = onboardingT[k];
    return tr ? { ...p, h: tr.h || p.h, d: tr.d || p.d } : p;
  }), [prefixSteps, onboardingT]);

  const combined = useMemo(() => [
    ...effPrefix.map((p) => ({ h: p.h, s: p.d ? [p.d] : [], rid: p.rid, kind: "prefix", silent: p.silent, dur: p.dur })),
    ...steps.map((s, k) => ({ ...s, rid: target[k], kind: "app" })),
  ], [effPrefix, steps, target]);
  useDemoTracking(app, lang, i, combined.length);
  const step = combined[i] || {};
  const me = step.kind === "app" && meTargets.includes(step.rid);

  const measure = useCallback(() => {
    const wrap = wrapRef.current; const tid = combined[i]?.rid;
    if (!wrap || !tid) { setBox(null); return; }
    const el = wrap.querySelector(`[data-rid="${tid}"]`);
    if (!el) { setBox(null); return; }
    const r = el.getBoundingClientRect(); const w = wrap.getBoundingClientRect();
    setBox({ top: r.top - w.top, left: r.left - w.left, width: r.width, height: r.height });
  }, [i, combined]);

  useEffect(() => { measure(); const id = setInterval(measure, 500); window.addEventListener("resize", measure); return () => { clearInterval(id); window.removeEventListener("resize", measure); }; }, [measure]);
  useEffect(() => { if (activeRef.current) activeRef.current.scrollIntoView({ behavior: "smooth", block: "center" }); }, [i, steps]);

  const advance = useCallback(() => setI((x) => (x + 1 < combined.length ? x + 1 : (setPlaying(false), x))), [combined.length]);
  const narrOf = (c) => (c && !c.silent ? [c.h, ...((c.s) || [])].filter(Boolean).join(". ") : "");
  const narrText = useMemo(() => narrOf(combined[i]), [combined, i]);
  const prefetch = useMemo(() => [1, 2, 3].map((d) => narrOf(combined[i + d])).filter(Boolean), [combined, i]);
  const { loading: narrLoading } = useNarrator({ text: narrText, playing, voiceOn, lang, onEnded: advance, stepKey: i, prefetch });

  // Advance on a timer when voice is off, OR for silent caveat steps even when voice is on
  // (they carry no narration, so the narrator can't drive the transition).
  useEffect(() => {
    if (!playing || !combined.length) return undefined;
    const silent = combined[i]?.silent;
    if (voiceOn && !silent) return undefined;
    const id = setTimeout(advance, combined[i]?.dur || 6000);
    return () => clearTimeout(id);
  }, [playing, i, combined, voiceOn, advance]);

  const go = (d) => { setPlaying(false); setI((x) => Math.min(Math.max(x + d, 0), combined.length - 1)); };

  return (
    <div data-testid="replica-tour">
      <div className="grid gap-5 lg:grid-cols-2">
        {/* replica screen */}
        <div>
          <div className="mb-2 text-xs font-semibold uppercase tracking-wide opacity-50">{L("replica_of_screen", "Replica of the live screen")}</div>
          <div ref={wrapRef} className="relative">
            {step.kind === "prefix" && PrefixPane ? <PrefixPane activeRid={step.rid} active={step} P={P} /> : <Pane step={i} me={me} activeRid={step.rid} P={P} />}
            {me && <div className="pointer-events-none absolute -top-2 left-1/2 z-40 -translate-x-1/2 rounded-full bg-[var(--primary)] px-3 py-0.5 text-[10px] font-semibold text-white shadow animate-in fade-in slide-in-from-top-1 duration-500" data-testid="replica-me-badge">{P("Master Enabler")}</div>}
            {box && <div className="pointer-events-none absolute z-20 rounded-md ring-4 ring-[var(--primary)] transition-all duration-500" style={{ top: box.top - 4, left: box.left - 4, width: box.width + 8, height: box.height + 8 }} data-testid="replica-ring" />}
            {box && <div className="pointer-events-none absolute z-30 transition-all duration-500" style={{ top: box.top + box.height - 4, left: box.left + box.width - 8 }} data-testid="replica-hand"><Hand size={26} className="rotate-[-18deg] fill-white text-[var(--primary)] drop-shadow-lg" /></div>}
          </div>
          <div className="mt-3 rounded-lg border border-[var(--primary)]/20 bg-[var(--primary)]/[0.04] p-3">
            <div className="text-[11px] font-semibold uppercase tracking-wide text-[var(--primary)]">{L("step", "Step")} {i + 1} / {combined.length}</div>
            <div className="mt-0.5 font-head text-base">{step.h}</div>
          </div>
        </div>
        {/* verbatim manual */}
        <div>
          <div className="mb-2 text-xs font-semibold uppercase tracking-wide opacity-50">{L("user_manual", "User manual (verbatim)")}</div>
          <div className="max-h-[520px] overflow-y-auto rounded-xl border border-black/10 bg-white p-4 dark:border-white/10 dark:bg-neutral-900" data-testid="replica-manual">
            {combined.map((s, k) => {
              const active = k === i;
              const newSection = k === 0 || combined[k - 1].kind !== s.kind;
              const label = s.kind === "prefix" ? (meSection || prefixLabel) : appLabel;
              return (
                <React.Fragment key={k}>
                  {newSection && <div className="mb-1 mt-3 text-[11px] font-semibold uppercase tracking-wide text-[var(--primary)] first:mt-0"><span className="rounded bg-[var(--primary)]/10 px-2 py-0.5">{label}</span></div>}
                  <button ref={active ? activeRef : null} onClick={() => { setPlaying(false); setI(k); }}
                    className={`mb-2 block w-full rounded-lg border px-4 py-3 text-start transition-all duration-300 ${active ? "border-[var(--primary)] bg-[var(--primary)]/[0.06]" : "border-transparent opacity-55 hover:opacity-100"}`} data-testid={`replica-step-${k}`}>
                    <div className="flex items-center gap-2 font-head text-sm font-semibold">{active && <span className="text-[var(--primary)]">►</span>}{s.h}</div>
                    {active && (s.kind === "prefix"
                      ? <p className="mt-1 text-[13px] opacity-80">{(s.s || [])[0]}</p>
                      : <ol className="mt-2 list-decimal space-y-1 ps-4 text-[13px] opacity-80">{(s.s || []).map((line, li) => <li key={li}>{line}</li>)}</ol>)}
                  </button>
                </React.Fragment>
              );
            })}
          </div>
        </div>
      </div>
      {/* controls */}
      <div className="mt-5 flex flex-wrap items-center gap-2" data-testid="replica-controls">
        <button onClick={() => go(-1)} disabled={i === 0} className="inline-flex items-center gap-1.5 rounded-md border border-black/15 px-3 py-2 text-sm lift disabled:opacity-40 dark:border-white/15" data-testid="replica-prev"><SkipBack size={15} /> {L("prev", "Prev")}</button>
        <button onClick={() => setPlaying((p) => !p)} className="inline-flex items-center gap-1.5 rounded-md bg-[var(--primary)] px-4 py-2 text-sm font-medium text-white lift" data-testid="replica-playpause">{playing ? <><Pause size={15} /> {L("pause", "Pause")}</> : <><Play size={15} /> {L("play", "Play")}</>}</button>
        <button onClick={() => setVoiceOn((v) => !v)} title={voiceOn ? "Turn voice off" : "Turn voice narration on"} className={`inline-flex items-center gap-1.5 rounded-md border px-3 py-2 text-sm lift ${voiceOn ? "border-[var(--primary)] bg-[var(--primary)]/10 text-[var(--primary)]" : "border-black/15 dark:border-white/15"}`} data-testid="replica-voice">{narrLoading && voiceOn ? <Loader2 size={15} className="animate-spin" /> : voiceOn ? <Volume2 size={15} /> : <VolumeX size={15} />} {L("voice", "Voice")}</button>
        <button onClick={() => go(1)} disabled={i === combined.length - 1} className="inline-flex items-center gap-1.5 rounded-md border border-black/15 px-3 py-2 text-sm lift disabled:opacity-40 dark:border-white/15" data-testid="replica-next">{L("next", "Next")} <SkipForward size={15} /></button>
        <button onClick={() => { setI(0); setPlaying(true); }} className="inline-flex items-center gap-1.5 rounded-md border border-black/15 px-3 py-2 text-sm lift dark:border-white/15" data-testid="replica-restart"><RotateCcw size={15} /> {L("restart", "Restart")}</button>
        <div className="ms-auto flex flex-wrap items-center gap-1">
          {combined.map((_, k) => <button key={k} onClick={() => { setPlaying(false); setI(k); }} className={`h-2 rounded-full transition-all ${k === i ? "w-6 bg-[var(--primary)]" : "w-2 bg-black/20 dark:bg-white/20"}`} data-testid={`replica-dot-${k}`} />)}
        </div>
      </div>
      <p className="mt-3 text-xs opacity-50" data-testid="replica-note">{L("note", note)}</p>
    </div>
  );
}
